export const CONSENT_VERSION = 'v1-4cats';
export const CONSENT_HANDOFF_VERSION = 'v1';
export const CONSENT_HANDOFF_SCOPE = 'mindset-landing-pages+web-assessment';
export const CONSENT_HANDOFF_PARAM = 'consent';
export const CONSENT_HANDOFF_MAX_AGE_MS = 30 * 60 * 1000;
export const CONSENT_REOPEN_EVENT = 'nerva:consent-reopen';
export const CONSENT_UPDATED_EVENT = 'nerva:consent-updated';

export type Consent = {
  necessary: true;
  analytics: boolean;
  advertising: boolean;
  communications: boolean;
  chosen: boolean;
  ts: string;
  version: string;
};

const STORAGE_KEY = 'consent.v1';
const COOKIE_NAME = 'consent_v1';
const MAX_HANDOFF_LENGTH = 4096;
const CLOCK_SKEW_MS = 60000;
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

export function isPortableConsent(value: unknown): value is Consent {
  if (!isRecord(value)) return false;
  return (
    value.necessary === true &&
    typeof value.analytics === 'boolean' &&
    typeof value.advertising === 'boolean' &&
    typeof value.communications === 'boolean' &&
    typeof value.chosen === 'boolean' &&
    typeof value.ts === 'string' &&
    Number.isFinite(Date.parse(value.ts)) &&
    value.version === CONSENT_VERSION
  );
}

function encodeBase64(value: string): string {
  return btoa(unescape(encodeURIComponent(value)));
}

function decodeBase64(value: string): string {
  return decodeURIComponent(escape(atob(value)));
}

function decodeBase64Url(value: string): string {
  const base64 = value.replace(/-/g, '+').replace(/_/g, '/');
  return decodeBase64(base64 + '='.repeat((4 - (base64.length % 4)) % 4));
}

export function decodeConsentHandoff(
  encoded: string,
  now: number = Date.now()
): Consent | undefined {
  try {
    if (encoded.length === 0 || encoded.length > MAX_HANDOFF_LENGTH) return undefined;
    const value = JSON.parse(decodeBase64Url(encoded)) as unknown;
    if (
      !isRecord(value) ||
      value.version !== CONSENT_HANDOFF_VERSION ||
      value.scope !== CONSENT_HANDOFF_SCOPE ||
      typeof value.issuedAt !== 'number'
    ) {
      return undefined;
    }
    if (value.issuedAt > now + CLOCK_SKEW_MS || now - value.issuedAt > CONSENT_HANDOFF_MAX_AGE_MS) {
      return undefined;
    }
    if (!isPortableConsent(value.consent) || !value.consent.chosen) {
      return undefined;
    }
    if (Date.parse(value.consent.ts) > now + CLOCK_SKEW_MS) return undefined;
    return value.consent;
  } catch {
    return undefined;
  }
}

function readStoredConsent(): unknown {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? 'null') as unknown;
  } catch {
    return undefined;
  }
}

function readCookieConsent(): unknown {
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${COOKIE_NAME}=([^;]+)`));
  if (match === null) return undefined;
  try {
    return JSON.parse(decodeBase64(match[1])) as unknown;
  } catch {
    return undefined;
  }
}

function cookieDomain(): string {
  // Only Nerva shares a parent domain with its landing page; other apps rely on fragment handoff.
  return location.hostname === 'nervaibs.com' || location.hostname.endsWith('.nervaibs.com')
    ? ' Domain=.nervaibs.com;'
    : '';
}

function writeConsent(consent: Consent): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));
  } catch {
    // The cookie remains available when browser storage is blocked.
  }

  const expires = new Date(Date.now() + 365 * 864e5).toUTCString();
  const secure = location.protocol === 'https:' ? ' Secure;' : '';
  document.cookie = `${COOKIE_NAME}=${encodeBase64(JSON.stringify(consent))};${cookieDomain()} Path=/; Expires=${expires}; SameSite=Lax;${secure}`;
}

function migrateExplicitRejection(value: unknown, now: number): Consent | undefined {
  if (!isRecord(value) || value.chosen !== true) return undefined;
  if (value.analytics !== false || value.advertising !== false || value.communications !== false) {
    return undefined;
  }
  const ts =
    typeof value.ts === 'string' && Number.isFinite(Date.parse(value.ts))
      ? value.ts
      : new Date(now).toISOString();
  if (Date.parse(ts) > now + CLOCK_SKEW_MS) return undefined;

  return {
    necessary: true,
    analytics: false,
    advertising: false,
    communications: false,
    chosen: true,
    ts,
    version: CONSENT_VERSION,
  };
}

function currentExplicitConsent(value: unknown, now: number): Consent | undefined {
  if (!isPortableConsent(value) || !value.chosen) return undefined;
  return Date.parse(value.ts) <= now + CLOCK_SKEW_MS ? value : undefined;
}

function readFragmentHandoff(now: number): Consent | undefined {
  const fragment = new URLSearchParams(location.hash.replace(/^#/, ''));
  const encoded = fragment.get(CONSENT_HANDOFF_PARAM);
  if (encoded === null) return undefined;

  fragment.delete(CONSENT_HANDOFF_PARAM);
  const remainingFragment = fragment.toString();
  history.replaceState(
    history.state,
    '',
    `${location.pathname}${location.search}${
      remainingFragment !== '' ? `#${remainingFragment}` : ''
    }`
  );
  return decodeConsentHandoff(encoded, now);
}

function defaultConsent(now: number): Consent {
  return {
    necessary: true,
    analytics: false,
    advertising: false,
    communications: false,
    chosen: false,
    ts: new Date(now).toISOString(),
    version: CONSENT_VERSION,
  };
}

function latestConsent(consents: Array<Consent | undefined>): Consent | undefined {
  return consents
    .filter((consent): consent is Consent => consent !== undefined)
    .sort((a, b) => Date.parse(b.ts) - Date.parse(a.ts))[0];
}

export function hasGlobalPrivacyControl(): boolean {
  return navigator.globalPrivacyControl === true;
}

function applyGlobalPrivacyControl(consent: Consent): Consent {
  if (!hasGlobalPrivacyControl()) return consent;
  return {
    ...consent,
    advertising: false,
    communications: false,
  };
}

let currentConsent = defaultConsent(Date.now());
let storageSyncInstalled = false;

function syncConsentFromStorage(event: StorageEvent): void {
  if (event.key !== STORAGE_KEY && event.key !== null) return;

  const now = Date.now();
  const stored = readStoredConsent();
  const cookie = readCookieConsent();
  const resolved = latestConsent([
    currentExplicitConsent(stored, now),
    currentExplicitConsent(cookie, now),
    migrateExplicitRejection(stored, now),
    migrateExplicitRejection(cookie, now),
  ]);

  currentConsent = applyGlobalPrivacyControl(resolved ?? defaultConsent(now));
  window.dispatchEvent(new Event(CONSENT_UPDATED_EVENT));
}

function installStorageSync(): void {
  if (storageSyncInstalled) return;
  storageSyncInstalled = true;
  window.addEventListener('storage', syncConsentFromStorage);
}

function initialize(now: number = Date.now()): Consent {
  installStorageSync();
  const stored = readStoredConsent();
  const cookie = readCookieConsent();
  const resolved = latestConsent([
    readFragmentHandoff(now),
    currentExplicitConsent(stored, now),
    currentExplicitConsent(cookie, now),
    migrateExplicitRejection(stored, now),
    migrateExplicitRejection(cookie, now),
  ]);

  currentConsent = applyGlobalPrivacyControl(resolved ?? defaultConsent(now));
  if (resolved !== undefined) writeConsent(currentConsent);
  return currentConsent;
}

function get(): Consent {
  return currentConsent;
}

function set(prefs: Partial<Consent>): Consent {
  currentConsent = applyGlobalPrivacyControl({
    ...currentConsent,
    ...prefs,
    necessary: true,
    chosen: true,
    ts: new Date().toISOString(),
    version: CONSENT_VERSION,
  });
  writeConsent(currentConsent);
  window.dispatchEvent(new Event(CONSENT_UPDATED_EVENT));
  return currentConsent;
}

export function hasAnyNonEssentialConsent(consent: Consent = currentConsent): boolean {
  return consent.analytics || consent.advertising || consent.communications;
}

export function consentCategoryPreferences(consent: Consent = currentConsent): {
  necessary: true;
  analytics: boolean;
  advertising: boolean;
  communications: boolean;
} {
  const unmanaged = !consent.chosen;
  const globalPrivacyControl = hasGlobalPrivacyControl();

  return {
    necessary: true,
    analytics: unmanaged || consent.analytics,
    advertising: unmanaged ? !globalPrivacyControl : consent.advertising,
    communications: unmanaged ? !globalPrivacyControl : consent.communications,
  };
}

export const ConsentManager = {
  initialize,
  get,
  set,
};
